import {
  Archive,
  BookOpen,
  CircleHelp,
  Flame,
  LogOut,
  Mic2,
  Radio,
  Search,
  Send,
  House,
  Anvil,
} from "lucide-react";
import type { ReactNode } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { outbox } from "../lib/outbox";

import type { User } from "firebase/auth";

import { AionMark } from "./AionMark";

const navigation = [
  { to: "/", label: "Home", icon: House },
  { to: "/ignis", label: "Ignis", icon: BookOpen },
  { to: "/incus", label: "Incus", icon: Anvil },
  { to: "/c", label: "Capture", icon: Mic2 },
  { to: "/q", label: "Ask", icon: CircleHelp },
];

export const AppShell = ({
  children,
  onSignOut,
  user,
}: {
  children: ReactNode;
  onSignOut: () => void;
  user: User;
}) => {
  const currentPath = window.location.pathname;
  const inLibrary = currentPath.startsWith("/ignis");
  const pendingCount = useLiveQuery(() => outbox.items.where("status").anyOf("ready", "uploading", "failed").count(), [], 0);

  return (
    <div className={`app-shell ${inLibrary ? "app-shell--library" : ""}`}>
      <aside className="side-rail">
        <AionMark />
        <nav className="side-rail__nav" aria-label="Primary">
          {navigation.map(({ icon: Icon, label, to }) => (
            <a
              aria-current={currentPath === to ? "page" : undefined}
              className={`rail-link ${
                currentPath === to ? "rail-link--active" : ""
              }`}
              href={to}
              key={to}
            >
              <Icon aria-hidden="true" size={18} strokeWidth={1.6} />
              <span>{label}</span>
            </a>
          ))}
        </nav>

        <div className="side-rail__rule" />
        <a className="rail-link" href="/outbox" aria-current={currentPath === "/outbox" ? "page" : undefined}><Send size={17} /><span>Outbox</span>{pendingCount ? <span className="outbox-count">{pendingCount}</span> : null}</a>

        <div className="side-rail__meta">
          <div>
            <Flame aria-hidden="true" size={16} />
            <span>Owned knowledge</span>
          </div>
          <div>
            <Archive aria-hidden="true" size={16} />
            <span>Immutable history</span>
          </div>
          <div>
            <Search aria-hidden="true" size={16} />
            <span>Source-aware</span>
          </div>
        </div>

        <div className="side-rail__account">
          <div className="account-avatar">
            {user.photoURL ? (
              <img alt="" src={user.photoURL} />
            ) : (
              user.displayName?.slice(0, 1) ?? "A"
            )}
          </div>
          <div className="account-copy">
            <strong>{user.displayName ?? "Andrés"}</strong>
            <span>{user.email}</span>
          </div>
          <button
            aria-label="Sign out"
            className="icon-button"
            onClick={onSignOut}
            type="button"
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      <main className="app-main">{children}</main>

      <nav className="bottom-nav" aria-label="Primary">
        {navigation.filter(({ to }) => ["/", "/c", "/q"].includes(to)).map(({ icon: Icon, label, to }) => (
          <a
            aria-current={currentPath === to ? "page" : undefined}
            className={`bottom-link ${
              currentPath === to ? "bottom-link--active" : ""
            }`}
            href={to}
            key={to}
          >
            <Icon aria-hidden="true" size={20} strokeWidth={1.7} />
            <span>{label}</span>
          </a>
        ))}
      </nav>

      <div className="network-indicator" aria-live="polite">
        <Radio size={10} />
        <span>{navigator.onLine ? "Online" : "Offline-safe"}</span>
        <a href="/outbox">Outbox{pendingCount ? ` (${pendingCount})` : ""}</a>
      </div>
    </div>
  );
};
